import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MovieCard } from "./MovieCard";
import { IconChevronLeft, IconChevronRight } from "@/components/icons/Icons";
import { RowSkeleton } from "@/components/skeleton/Skeletons";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { GRID_COLS } from "@/components/skeleton/Skeletons";
import { useSentinel } from "@/hooks/useInView";

export function MovieRow({ title, items, href, testId = "movie-row", showProgress = false, eager = false }) {
  const [ref, inView] = useInView({ rootMargin: "400px" });
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const update = () => {
    const el = scroller.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  };
  useEffect(update, [inView, items]);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  if (!items?.length) return null;
  const visible = eager || inView;

  return (
    <section ref={ref} className="pk-cv group/row relative py-3 sm:py-4" data-testid={testId} aria-label={title}>
      {!visible ? (
        <RowSkeleton count={7} />
      ) : (
        <>
          <div className="pk-container mb-3 flex items-end justify-between gap-4">
            <h2 className="text-[20px] font-extrabold tracking-[-0.015em] text-white sm:text-[22px]">{title}</h2>
            {href && (
              <Link to={href} className="inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-[13.5px] font-semibold text-white/60 transition-colors hover:text-white" data-testid={`${testId}-see-all`}>
                Barchasi <IconChevronRight size={15} />
              </Link>
            )}
          </div>
          <div className="relative">
            <div ref={scroller} onScroll={update} className="pk-scroll-x pk-bleed flex snap-x snap-proximity gap-3 overflow-x-auto pb-3 pt-2 sm:gap-4">
              {items.map((m, i) => (
                <MovieCard key={m.id} movie={m} inRow index={i} showProgress={showProgress} />
              ))}
            </div>
            <RowArrow side="left" hidden={edges.start} onClick={() => scrollBy(-1)} />
            <RowArrow side="right" hidden={edges.end} onClick={() => scrollBy(1)} />
          </div>
        </>
      )}
    </section>
  );
}

function RowArrow({ side, hidden, onClick }) {
  const Icon = side === "left" ? IconChevronLeft : IconChevronRight;
  return (
    <button
      onClick={onClick}
      aria-label={side === "left" ? "Oldingi" : "Keyingi"}
      tabIndex={-1}
      className={cn(
        "pk-glass absolute top-[38%] z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-white opacity-0 transition-opacity duration-300 lg:grid",
        side === "left" ? "left-3" : "right-3",
        hidden ? "pointer-events-none" : "group-hover/row:opacity-100"
      )}
    >
      <Icon size={20} />
    </button>
  );
}

/** Responsive grid with infinite scroll sentinel. */
export function MovieGrid({ items, hasMore, loadMore, loadingMore, testId = "movie-grid" }) {
  const sentinel = useSentinel(() => hasMore && !loadingMore && loadMore?.(), hasMore);
  return (
    <>
      <div className={GRID_COLS} data-testid={testId}>
        {items.map((m, i) => (
          <MovieCard key={m.id} movie={m} index={i % 24} />
        ))}
      </div>
      {hasMore && <div ref={sentinel} className="h-4" aria-hidden="true" />}
      {loadingMore && (
        <div className={cn(GRID_COLS, "mt-6")}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <div className="pk-shimmer aspect-[2/3] rounded-[20px]" />
              <div className="pk-shimmer mt-3 h-[14px] w-4/5 rounded-full" />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
